export class CheckoutTimeoutError extends Error {
  constructor() {
    super('checkout_request_timeout');
    this.code = 'checkout_request_timeout';
  }
}

// Bound the whole operation, including response bodies. Aborting a browser
// request does not establish whether a remote write completed; callers retain
// their durable order/attempt identity and never replay from this helper.
export async function withCheckoutDeadline(operation, { timeoutMs = 60000, signal } = {}) {
  const controller = new AbortController();
  let rejectDeadline;
  const deadline = new Promise((_, reject) => { rejectDeadline = reject; });
  const cancel = (reason) => {
    rejectDeadline(reason);
    controller.abort(reason);
  };
  const onAbort = () => cancel(signal.reason || new Error('checkout_request_cancelled'));
  const timer = setTimeout(() => cancel(new CheckoutTimeoutError()), timeoutMs);
  signal?.addEventListener('abort', onAbort, { once: true });
  try {
    if (signal?.aborted) onAbort();
    return await Promise.race([
      deadline,
      Promise.resolve().then(() => {
        if (controller.signal.aborted) throw controller.signal.reason;
        return operation(controller.signal);
      }),
    ]);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
}

export function checkoutFetch(input, init = {}, { fetcher = globalThis.fetch, timeoutMs = 60000 } = {}) {
  return withCheckoutDeadline(async (signal) => {
    const response = await fetcher(input, { ...init, signal });
    const body = response.body === null ? null : await response.arrayBuffer();
    if (signal.aborted) throw signal.reason;
    return new Response(body, { status: response.status, statusText: response.statusText, headers: response.headers });
  }, { timeoutMs, signal: init.signal });
}

export function loadCheckoutImage(src, { timeoutMs = 15000, createImage = () => new Image() } = {}) {
  return withCheckoutDeadline((signal) => new Promise((resolve, reject) => {
    const image = createImage();
    const cleanup = () => {
      image.onload = null;
      image.onerror = null;
      signal.removeEventListener('abort', onAbort);
    };
    const onAbort = () => {
      cleanup();
      image.src = '';
      reject(signal.reason);
    };
    image.onload = () => { cleanup(); resolve(image); };
    image.onerror = () => { cleanup(); reject(new Error('checkout_image_unavailable')); };
    signal.addEventListener('abort', onAbort, { once: true });
    image.crossOrigin = 'anonymous';
    image.src = src;
  }), { timeoutMs });
}
